import { Flex, Spacing, Text } from '@billy/design-system'
import { ReactElement, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'

import { BankAccountSource } from '@features/bankAccounts/enums/bankAccountSource'
import { BankLineStatus } from '@features/reconciliation/enums/bankLineStatus'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useLocale } from '../../../../../../hooks/useLocale'
import { useSandboxBankAccountByBookkeepingAccountId } from '../../hooks/useSandboxBankAccountByBookkeepingAccountId'
import { useSandboxBankLineSuggestionsMap } from '../../hooks/useSandboxBankLineSuggestionsMap'
import { useSandboxBankTransactions } from '../../hooks/useSandboxBankTransactions'
import { useSandboxBankTransactionsFilters } from '../../hooks/useSandboxBankTransactionsFilters'
import { useSandboxBookkeepingItems } from '../../hooks/useSandboxBookkeepingItems'
import { getBankTransactionsPermissions } from '../../utils/getBankTransactionsPermissions'
import { BankTransaction } from './elements/BankTransaction'
import { BankTransactionsEmptyState } from './elements/BankTransactionsEmptyState'
import { getBankTransactionsEmptyStateVariant } from './elements/BankTransactionsEmptyState/utils/getBankTransactionsEmptyStateVariant'
import { LoadingSkeleton } from './elements/LoadingSkeleton'
import { PendingBankTransactionsSection } from './elements/PendingBankTransactionsSection'
import * as Styled from './styles'
import { formatTransactionGroupDate } from './utils/formatTransactionGroupDate'
import { groupTransactionsByDate } from './utils/groupTransactionsByDate'

export const BankTransactionsList = (): ReactElement => {
  const { t } = useTranslation()
  const { code } = useLocale()
  const [filters] = useSandboxBankTransactionsFilters()
  const { accountId, statusFilter, searchQuery, minAmount, maxAmount, transactionTypeFilter, period } = filters
  const { currency, source } = useSandboxBankAccountByBookkeepingAccountId(accountId)
  const isShineBankingAccount = source === BankAccountSource.SHINE_BANKING
  const { bankTransactions, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useSandboxBankTransactions({
    ...filters,
    status: BankLineStatus.Booked,
  })
  const { bankTransactions: pendingTransactions, isLoading: isLoadingPending } = useSandboxBankTransactions({
    ...filters,
    enabled: isShineBankingAccount,
    status: BankLineStatus.Pending,
  })
  const { ref: lastItemRef, inView } = useInView()
  const { organization } = useUserOrganization()
  const { hasBankReconciliationAutoMatchScope } = getBankTransactionsPermissions()
  const { items: bookkeepingItems } = useSandboxBookkeepingItems({
    accountId,
    enabled: hasBankReconciliationAutoMatchScope,
  })
  const { suggestionsByBankLineId } = useSandboxBankLineSuggestionsMap({
    accountId,
    bankLines: bankTransactions,
    bookkeepingItems,
    enabled: hasBankReconciliationAutoMatchScope,
    organizationId: organization?.id,
  })

  const groupedTransactions = useMemo(() => groupTransactionsByDate(bankTransactions), [bankTransactions])
  const shouldShowLoadingSkeleton = isLoading || isFetchingNextPage || isLoadingPending
  const shouldShowEmptyState =
    !!accountId && !shouldShowLoadingSkeleton && groupedTransactions.length === 0 && pendingTransactions.length === 0

  const emptyStateVariant = useMemo(
    () =>
      getBankTransactionsEmptyStateVariant({
        statusFilter,
        searchQuery,
        minAmount,
        maxAmount,
        transactionTypeFilter,
        period,
      }),
    [statusFilter, searchQuery, minAmount, maxAmount, transactionTypeFilter, period],
  )

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  if (shouldShowEmptyState) {
    return <BankTransactionsEmptyState variant={emptyStateVariant} />
  }

  return (
    <>
      <Flex flexDirection="column" gap={Spacing.S24}>
        <PendingBankTransactionsSection pendingTransactions={pendingTransactions} currency={currency} />
        {groupedTransactions.map(({ date, transactions }) => (
          <Flex flexDirection="column" key={date}>
            <Styled.DateGroupHeader>
              <Text colorVariant="discreet">{formatTransactionGroupDate(date, t, code)}</Text>
            </Styled.DateGroupHeader>
            {transactions.map((transaction) => (
              <BankTransaction
                key={transaction.id}
                transaction={transaction}
                currency={currency}
                hasSuggestedMatch={suggestionsByBankLineId.has(transaction.id)}
              />
            ))}
          </Flex>
        ))}
        {shouldShowLoadingSkeleton && <LoadingSkeleton />}
      </Flex>
      {hasNextPage && <div ref={lastItemRef} />}
    </>
  )
}
